import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { onAnimationEnd, changeMetaThemeColor } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * With a `docked-min-width` attribute, the drawer docks to the right of the page on wide screens
 * instead of opening as a modal: the page is pushed left, stays scrollable and clickable, and no
 * overlay is shown. Docked mode shows the dialog non-modally in the top layer (`popover="manual"`),
 * so it isn't clipped by the header's containment or hidden by the sticky header's fade.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** The modal close behaviour from DialogComponent, kept for when the drawer isn't docked. */
  #closeModalDialog = this.closeDialog;

  /** @type {MediaQueryList | null} */
  #dockedMedia = null;

  /** Whether this drawer is currently open in docked mode. */
  #docked = false;

  /** Whether this drawer is currently open as a modal (DialogComponent locks the page scroll). */
  #modalOpen = false;

  /** @type {HTMLElement | null} The element to refocus when the docked drawer closes. */
  #dockedReturnFocus = null;

  /** @type {string | null} */
  #previousThemeColor = null;

  /** Re-checks the sticky footer when the drawer's content is re-rendered while open. */
  #contentObserver = new MutationObserver((mutations) => {
    // Ignore the countdown ticking every second.
    if (mutations.every((mutation) => mutation.target.parentElement?.closest('dw-cart-timer'))) return;
    if (this.#stickyFrame) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = 0;
      this.#updateStickyState();
    });
  });

  #stickyFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
    // 'cancel' and 'close' don't bubble; capture them so a re-rendered dialog is still covered.
    this.addEventListener('cancel', this.#handleNativeCancel, true);
    this.addEventListener('close', this.#handleNativeClose, true);

    const dockedMinWidth = Number(this.getAttribute('docked-min-width'));
    if (dockedMinWidth) {
      this.#dockedMedia = window.matchMedia(`(min-width: ${dockedMinWidth}px)`);
      this.#dockedMedia.addEventListener('change', this.#handleDockedMediaChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.removeEventListener('cancel', this.#handleNativeCancel, true);
    this.removeEventListener('close', this.#handleNativeClose, true);
    this.#dockedMedia?.removeEventListener('change', this.#handleDockedMediaChange);
    document.removeEventListener('keydown', this.#handleDockedKeyDown);
    this.#contentObserver.disconnect();

    // The header can be re-rendered (theme editor, apps) while the drawer is docked open.
    if (this.#docked) {
      this.#docked = false;
      document.documentElement.removeAttribute('data-dw-cart-docked');
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Shows the drawer, docked on wide screens when enabled, otherwise as a modal.
   */
  showDialog() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    if (!this.#canDock()) {
      this.#modalOpen = true;
      super.showDialog();
      this.#tintThemeColor();
      return;
    }

    requestAnimationFrame(() => {
      if (dialog.open) return;

      const opener = document.activeElement;
      dialog.show();

      try {
        dialog.showPopover();
      } catch (error) {
        // Handled below: without the top layer the drawer could be clipped by the header.
      }

      if (!dialog.matches(':popover-open')) {
        dialog.close();
        this.#modalOpen = true;
        super.showDialog();
        this.#tintThemeColor();
        return;
      }

      this.#docked = true;
      this.#dockedReturnFocus = opener instanceof HTMLElement && opener !== document.body ? opener : null;
      document.documentElement.setAttribute('data-dw-cart-docked', '');
      document.addEventListener('keydown', this.#handleDockedKeyDown);
      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  /**
   * Closes the drawer, whichever way it was opened.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog?.open || dialog.classList.contains('dialog-closing')) return;

    if (!this.#docked) {
      await this.#closeModalDialog();
      this.#modalOpen = false;
      this.#restoreThemeColor();
      return;
    }

    document.removeEventListener('keydown', this.#handleDockedKeyDown);

    // Restart the closing animation, as DialogComponent does for the modal drawer.
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // Removing the attribute slides the page back while the drawer animates out.
    document.documentElement.removeAttribute('data-dw-cart-docked');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    try {
      dialog.hidePopover();
    } catch (error) {
      // Not showing as a popover; nothing to hide.
    }
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#docked = false;
    this.#restoreDockedFocus();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * The browser's own close request on the modal drawer (Escape while focus is on <body>, e.g. after a
   * re-render removed the focused control, or Android's back gesture). Route it through the animated
   * close so the page scroll lock and browser tint are undone.
   *
   * @param {Event} event
   */
  #handleNativeCancel = (event) => {
    if (event.target !== this.refs.dialog || this.#docked) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * When the browser closes the modal drawer without letting us cancel (a non-cancelable close request),
   * clean up what DialogComponent's close would have.
   *
   * @param {Event} event
   */
  #handleNativeClose = (event) => {
    const { dialog } = this.refs;
    // Only for the modal drawer: closeDialog clears the body lock before dialog.close(), so a still-fixed
    // body then means a native close. Another modal still open (search, quick add) owns its own lock.
    if (event.target !== dialog || !this.#modalOpen || document.body.style.position !== 'fixed') return;
    if (document.querySelector('dialog:modal')) return;

    this.#modalOpen = false;

    const top = -parseInt(document.body.style.top || '0', 10) || 0;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top, behavior: 'instant' });
    dialog?.classList.remove('dialog-closing');
    this.#restoreThemeColor();
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * @returns {boolean} Whether the drawer should open docked right now.
   */
  #canDock() {
    const { dialog } = this.refs;

    return Boolean(
      this.#dockedMedia?.matches &&
        dialog?.hasAttribute('popover') &&
        typeof dialog.showPopover === 'function' &&
        // The theme's popover polyfill can't put a dialog in the top layer; only dock with native support.
        CSS.supports('selector(:popover-open)')
    );
  }

  /**
   * Returns focus to whatever opened the docked drawer (non-modal dialogs don't do this reliably).
   */
  #restoreDockedFocus() {
    const { dialog } = this.refs;
    const target =
      this.#dockedReturnFocus?.isConnected
        ? this.#dockedReturnFocus
        : /** @type {HTMLElement | null} */ (this.querySelector('[data-testid="cart-drawer-trigger"]'));
    this.#dockedReturnFocus = null;

    const active = document.activeElement;
    if (target && (active === document.body || active === null || dialog?.contains(active))) {
      target.focus({ preventScroll: true });
    }
  }

  /**
   * Closes a docked drawer when the screen gets too narrow for docking.
   */
  #handleDockedMediaChange = () => {
    if (!this.#dockedMedia?.matches && this.#docked) {
      this.closeDialog();
    }
  };

  /**
   * Escape closes the docked drawer (non-modal dialogs don't get this for free), unless something
   * else on the page should handle it first.
   *
   * @param {KeyboardEvent} event
   */
  #handleDockedKeyDown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;

    const { dialog } = this.refs;

    // Let another open light-dismiss popover (e.g. the account menu) or a modal on top (search, zoom, quick add)
    // close natively. Pinned `popover="manual"` elements, such as Shopify's preview bar, never close on Escape.
    const otherPopovers = [...document.querySelectorAll(':popover-open')].filter((element) => element !== dialog);
    if (otherPopovers.some((element) => element instanceof HTMLElement && element.popover !== 'manual')) return;
    if (document.querySelector('dialog:modal')) return;

    // Only handle Escape aimed at the drawer: from inside it, the cart button, or nothing focused.
    // Other page layers (country selector, video modal, search) keep their own Escape.
    const { target } = event;
    const ownsEscape =
      target === document.body ||
      (target instanceof Node && dialog?.contains(target)) ||
      (target instanceof Element && target.closest('[data-testid="cart-drawer-trigger"]'));
    if (!ownsEscape) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Matches the browser chrome (e.g. iOS Safari's toolbar) to the drawer background while it is open.
   */
  #tintThemeColor() {
    const { dialog } = this.refs;
    const meta = document.head.querySelector('meta[name="theme-color"]');
    if (!dialog || !meta) return;

    requestAnimationFrame(() => {
      const background = getComputedStyle(dialog).backgroundColor;
      if (!background) return;

      this.#previousThemeColor = meta.getAttribute('content') ?? '';
      changeMetaThemeColor(background);
    });
  }

  #restoreThemeColor() {
    if (this.#previousThemeColor === null) return;

    const meta = document.head.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', this.#previousThemeColor);
    this.#previousThemeColor = null;
  }

  #handleOpen = () => {
    this.#updateStickyState();

    const { dialog } = this.refs;
    if (dialog) this.#contentObserver.observe(dialog, { childList: true, subtree: true });
  };

  #handleClose = () => {
    this.#contentObserver.disconnect();
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // No footer (empty cart): reset to the default so a footer rendered in place starts pinned.
      dialog.setAttribute('cart-summary-sticky', 'true');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
